import _ from 'lodash'
import { Graph, Node } from '@antv/x6'
import config from './config'
import nodePool from '@ps/bridge/qc/node-pool'
import { transformXyLogic2Ui } from '@ps/bridge/qc/node'
import { isScene } from './utils'
import store from '@ps/store'
import iframe from '@ps/bridge/iframe/iframe'
import * as qc from 'qc'
import {
  QcNode,
  StoryBoardNode
} from '@ps/store/workbench/storyboard/index.type'
import { getSceneNodeByUuid } from '@ps/bridge/qc/base'

type TreeNode = {
  node: Node.Metadata;
  parent: Node.Metadata | null;
  children:
    | ({ node: Node.Metadata; parent: Node.Metadata | null } | null)[]
    | null;
};

export function hideSceneEditor(): void {
  const ele = document.getElementById('scene-editor') as HTMLElement
  ele.style.opacity = '0'
  ele.style.zIndex = '-10'
}

export function showSceneEditor(): void {
  // 不能直接隐藏 scene-editor，因为可能会导致 iframe 重新渲染
  const ele = document.getElementById('scene-editor') as HTMLElement
  ele.style.opacity = '1'
  ele.style.zIndex = '1'
}

function postOrderTraverseNode(
  node: TreeNode,
  callback: (node: TreeNode) => void
) {
  if (node) {
    if (node.children) {
      _.eachRight(node.children, child => {
        postOrderTraverseNode(child as TreeNode, callback)
      })
      callback(node)
    }
  }
}

function preOrderTraverseNode(
  node: TreeNode,
  callback: (node: TreeNode, level: number) => void,
  level: number
) {
  if (node) {
    callback(node, level)
    if (node.children) {
      _.each(node.children, child => {
        preOrderTraverseNode(child as TreeNode, callback, level + 1)
      })
    }
  }
}

// const storyBoard: {
//   // 场景相关信息
//   scenes: {
//     [key: string]: {
//       x: number;
//       y: number;
//     };
//   };
// } = {}

function getSceneWhichHadSwitchEvent(
  scenes: Node.Metadata[],
  nodes: Node.Metadata[],
  edges: Node.Metadata[]
) {
  // 找出场景（与场景后代节点）与场景的跳转关系表
  const map: {
    [key: string]: {
      sceneId: string; // 场景 id
      sceneIndex: number;
      next: string[]; // 该场景会跳转到的场景的 id 列表
    };
  } = {}
  const allNodes = [...scenes, ...nodes]
  allNodes.forEach(item => {
    const { sceneIndex, sceneId, switchSceneId } = item.data
    if (map[sceneIndex]) {
      switchSceneId && map[sceneIndex].next.push(switchSceneId)
      map[sceneIndex].next = _.uniq(map[sceneIndex].next)
    } else {
      map[sceneIndex] = {
        sceneId,
        sceneIndex,
        next: switchSceneId ? [switchSceneId] : []
      }
    }
  })
  // 过滤既不是连线起点也不是连线终点的场景
  _.each(map, item => {
    if (
      edges.every(
        edge =>
          edge.data.sceneId !== item.sceneId &&
          edge.data.switchSceneId !== item.sceneId
      )
    ) {
      delete map[item.sceneIndex]
    }
  })
  return map
}

type SceneConfig = {
  sceneId: string; // 场景 id
  sceneIndex: number; // 场景索引
  next: string[]; // 该场景会跳转到的场景的 id 列表
};

function createTree(
  graph: Node.Metadata[],
  sceneWhichHadSwitchEventArray: SceneConfig[]
) {
  const searched: string[] = []

  function rec(scene: SceneConfig | undefined, pid: string): TreeNode | null {
    if (!scene) return null
    if (searched.includes(scene.sceneId)) return null
    const { sceneId, next } = scene
    searched.push(sceneId)

    const node = graph.find(item => item.id === sceneId)
    const parent = graph.find(item => item.id === pid)

    // 有节点 id，但是找不到节点则返回
    if (!node) {
      console.log(`没有找到节点${sceneId}对应的节点配置`)
      return null
    }

    return {
      node,
      parent: parent || null,
      children: next.map(item => {
        return rec(
          sceneWhichHadSwitchEventArray.find(j => j.sceneId === item),
          sceneId
        )
      })
    }
  }

  return rec
}

/**
 * 通过节点与事件表生成 layout 数据，用在初始化或销毁后重新渲染的时候使用
 * @param data
 */
export function cookSceneNodeLayoutData(data: {
  nodes: Node.Metadata[];
  scenes: Node.Metadata[];
  edges: Node.Metadata[];
  width: number;
}): void {
  const { nodes, edges, scenes, width } = data
  const sceneWhichHadSwitchEventMap = getSceneWhichHadSwitchEvent(
    scenes,
    nodes,
    edges
  )
  // 有连线的场景（排序，保证顺序正确）
  const sceneWhichHadSwitchEventArray = _.sortBy(
    Object.values(sceneWhichHadSwitchEventMap),
    item => item.sceneIndex
  )
  const rec = createTree(
    [...scenes, ...nodes, ...edges],
    sceneWhichHadSwitchEventArray
  )

  // 用游戏的场景的宽高作为故事板场景的宽高
  const sceneWidth = scenes[0].width
  const sceneHeight = scenes[0].height
  if (!sceneWidth || !sceneHeight) {
    throw new Error('场景节点的宽高不能为空')
  }

  const tree = rec(sceneWhichHadSwitchEventArray[0], '')
  if (tree) {
    // 使用先序遍历算出 x
    preOrderTraverseNode(
      tree,
      (node: TreeNode, level: number) => {
        // level 为 0 说明是第一个节点
        node.node.x =
          level === 0
            ? config.sceneMargin
            : config.sceneMargin + (sceneWidth + config.sceneMargin) * level
      },
      0
    )

    // 使用后序遍历算出 y
    let index = 0
    postOrderTraverseNode(tree, (node: TreeNode) => {
      // 过滤空子节点保证布局数据正常
      if (node.children && !_.isEmpty(node.children.filter(item => Boolean(item)))) {
        const length = node.children.length
        const firstChild = node.children[0]
        const max = Math.max(...node.children.map(item => item?.node.y || 0))
        const min = Math.min(...node.children.map(item => item?.node.y || 0))

        // 只有一个子节点时，取第一个节点的 y
        if (length === 1) {
          node.node.y = firstChild?.node.y
        } else {
          // 有多个节点，居中
          node.node.y = max - (max - min) / 2
        }
      } else {
        node.node.y = (sceneHeight + config.sceneMargin) * index
        index += 1
      }
    })
  }

  // 没有连线的场景
  const sceneWhichHadNotSwitchEventArray = _.sortBy(
    scenes
      .filter(scene =>
        sceneWhichHadSwitchEventArray.every(item => item.sceneId !== scene.id)
      )
      .map(item => {
        return {
          sceneId: item.id,
          sceneIndex: item.data.sceneIndex
        }
      }),
    item => item.sceneIndex
  )

  // 如果没有连线的场景，则 maxY 强制设置为 0
  const offsetTop = _.isEmpty(sceneWhichHadSwitchEventArray)
    ? 0
    : Math.max(
      ...scenes
        .filter(item => isScene(item.shape as string))
      // @ts-ignore
        .map(item => item.y + item.height)
    ) || 0
  let x = 0
  let y = offsetTop
  sceneWhichHadNotSwitchEventArray.forEach((item, index) => {
    const sceneNode = scenes.find(scene => scene.id === item.sceneId)
    if (sceneNode) {
      // 如果 x 为 0，说明是当前行的第一个节点，所以不需要算 sceneWidth
      sceneNode.x =
        x === 0 ? x + config.sceneMargin : x + sceneWidth + config.sceneMargin
      sceneNode.y = y + config.sceneMargin
      x = sceneNode.x
      // 如果下一个节点会超出视口，则换行，换行的话要将 x 设置为初始值
      if (sceneNode.x + sceneWidth > width) {
        y = sceneNode.y + sceneHeight
        x = 0
      }
    }
  })
}

/**
 * 计算节点在故事板中的位置（非场景节点），要保证节点已渲染
 * @param id
 * @param sceneNode
 * @param useCache 是否使用缓存数据
 */
export function cookDagNodePosition({
  id,
  sceneNode,
  useCache = false
}: {
  id: string; // x6 节点 id，同样也是 qc.node 的 uuid
  sceneNode: Node; // x6 场景节点
  useCache: boolean;
}): {
  x: number; // 节点在故事板中的 x 值
  y: number;
  sceneX: number; // 节点在当前场景节点的 x 值
  sceneY: number;
} | null {
  if (useCache) {
    const position = sceneNode.position()
    const qcNodePosition = getQcNodePositionFormLayoutCache(id)
    return {
      x: position.x + Number((qcNodePosition.x / config.ratio).toFixed(2)),
      y: position.y + Number((qcNodePosition.y / config.ratio).toFixed(2)),
      sceneX: qcNodePosition.x / config.ratio,
      sceneY: qcNodePosition.y / config.ratio
    }
  }
  const qcSceneNode = getSceneNodeByUuid(sceneNode.id)
  if (!qcSceneNode) {
    console.error('找不到该节点对应的场景节点')
    return null
  }
  // 因为计算 transformXyLogic2Ui 时，当前节点必须渲染出来
  // 有些情况当前节点的场景节点没有被渲染出来，所以需要将场景节点先渲染出来
  const visible = qcSceneNode.visible
  qcSceneNode.visible = true
  // 节点的逻辑重心是在中心位置，视觉重心是在左上角，所以显示时需要添加补偿
  const { transformX, transformY } = transformXyLogic2Ui(nodePool.find(id))
  const position = sceneNode.position()
  qcSceneNode.visible = visible
  return {
    x: position.x + Number((transformX / config.ratio).toFixed(2)),
    y: position.y + Number((transformY / config.ratio).toFixed(2)),
    sceneX: transformX / config.ratio,
    sceneY: transformY / config.ratio
  }
}

/**
 * 布局
 * @param graph
 * @param sceneId 如果有 sceneId，则布局该场景的所有节点；没有则布局所有节点
 */
export function layout({
  graph,
  sceneId
}: {
  graph: Graph;
  sceneId?: string;
}): void {
  const cells = graph.getCells()
  const nodes = cells
    .filter(cell =>
      [config.nodeTypes.node, config.nodeTypes.scene].includes(cell.data.shape)
    )
    .filter(cell => {
      // 如果有场景 ID，就过滤出当前场景的节点，没有就不过滤
      if (sceneId) return cell.data.sceneId === sceneId
      return true
    }) as Node[]

  if (!nodes) return

  nodes.forEach(cell => {
    const sceneNode = nodes.find(
      item => cell.data.sceneId === item.data.sceneId
    )
    if (sceneNode) {
      if (cell.data.isSceneNode) {
        const { x, y } = cell.position()
        cell.position(x, y)
      } else {
        const position = cookDagNodePosition({
          id: cell.id,
          sceneNode,
          useCache: true
        })
        const { width, height } = sceneNode.size()
        if (position) {
          let { x, y } = position
          const { sceneX, sceneY } = position
          const node = nodePool.find(cell.id)
          if (
            sceneX >= width ||
              sceneY >= height ||
              sceneX + node.width / config.ratio <= 0 ||
              sceneY + node.height / config.ratio <= 0
          ) {
            // 如果在场景节点之外的话，则在故事板中不展示该节点
            cell.setVisible(false)
          } else {
            const size = cell.size()
            sceneX < 0 && (x = sceneNode.position().x)
            sceneY < 0 && (y = sceneNode.position().y)
            size.width + sceneX >= width && (size.width = width - sceneX + (sceneX < 0 ? sceneX : 0))
            size.height + sceneY >= height && (size.height = height - sceneY + (sceneY < 0 ? sceneY : 0))
            cell.position(x, y)
            cell.resize(size.width, size.height)
          }
        }
      }
    }
  })
}

// 新增的场景节点的位置 = 现有场景节点中处于最底部的节点的左下方
export function getPositionForNewSceneNode({
  qcNode,
  resolution
}: {
  qcNode: qc.Node;
  resolution: string;
}): {
  x: number;
  y: number;
} {
  const scenes = iframe.gamePlay.children
  const data: QcNode[] = Object.values(
    store.state.workspace.storyboard.layout[resolution]
  )
  const cloneLayoutData: QcNode[] = _.cloneDeep(data).filter((item: QcNode) => {
    // 过滤掉不存在的节点的数据，因为可能存在脏数据
    // nodePool 可以查找到节点，不代表节点存在
    return scenes.some(scene => scene.uuid === item.id)
  })
  return {
    x: Math.min(...cloneLayoutData.map(item => item.x)),
    y:
      Math.max(...cloneLayoutData.map(item => item.y)) +
      qcNode.height / config.ratio +
      config.sceneMargin
  }
}

/**
 * nodesLayout 必须得保证是正确的
 * @param uuid
 */
export function getQcNodePositionFormLayoutCache(
  uuid: string
): {
  x: number;
  y: number;
} {
  const layout = store.state.workspace.sceneManager.nodesLayout
  const adapterResolution = store.state.workspace.game.device.adapterResolution
  // qc.node 的 x、y 要提前算好，因为隐藏元素返回的 x、y 不正确
  return layout[uuid][adapterResolution] as StoryBoardNode
}
